import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, jobService } from '../services/api';
import { JobStatus, JobType } from '../types';
import type { JobSummaryDto } from '../types';

const JOB_STATUS_LABELS: Record<number, string> = {
  [JobStatus.Draft]:             'Draft',
  [JobStatus.PendingReview]:     'Pending Review',
  [JobStatus.Active]:            'Active',
  [JobStatus.Quoted]:            'Quoted',
  [JobStatus.Accepted]:          'Accepted',
  [JobStatus.ContractGenerated]: 'Contract Ready',
  [JobStatus.InProgress]:        'In Progress',
  [JobStatus.Completed]:         'Completed',
  [JobStatus.PodVerified]:       'POD Verified',
  [JobStatus.PaymentReleased]:   'Paid',
  [JobStatus.ReviewUnlocked]:    'Review Unlocked',
  [JobStatus.Cancelled]:         'Cancelled',
  [JobStatus.Rejected]:          'Rejected',
};

const JOB_TYPE_LABELS: Record<number, string> = {
  [JobType.SameDayDelivery]:    'Same-Day',
  [JobType.ScheduledDelivery]:  'Scheduled',
  [JobType.RelayDelivery]:      'Relay',
  [JobType.VehicleRepair]:      'Repair',
  [JobType.Removal]:            'Removal',
  [JobType.FreightHaulage]:     'Haulage',
};

const ACTIVE_STATUSES = new Set([
  JobStatus.PendingReview, JobStatus.Active, JobStatus.Quoted,
  JobStatus.Accepted, JobStatus.ContractGenerated, JobStatus.CarrierAccepted,
  JobStatus.InProgress, JobStatus.Paused,
]);
const CLOSED_STATUSES = new Set([
  JobStatus.Completed, JobStatus.PodVerified, JobStatus.PaymentReleased, JobStatus.ReviewUnlocked,
]);
const CANCELLED_STATUSES = new Set([JobStatus.Cancelled, JobStatus.Rejected]);

function statusColor(s: JobStatus): { bg: string; color: string } {
  if (ACTIVE_STATUSES.has(s))    return { bg: '#dbeafe', color: '#1e40af' };
  if (CLOSED_STATUSES.has(s))    return { bg: '#f3f4f6', color: '#374151' };
  if (CANCELLED_STATUSES.has(s)) return { bg: '#fee2e2', color: '#991b1b' };
  return { bg: '#d1fae5', color: '#065f46' };
}

export default function MyJobsPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') ?? 'all';  // 'all' | 'active' | 'quoted'
  const { getToken, account } = useAppAuth();

  const [jobs, setJobs]         = useState<JobSummaryDto[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState<'active' | 'drafts' | 'closed' | 'cancelled'>('active');
  const [publishing, setPublishing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!account) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      const svc = jobService(api);
      const data = mode === 'quoted'
        ? await svc.getMyQuotedJobs()
        : await svc.getMyJobs();
      setJobs(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, mode]);

  useEffect(() => { load(); }, [load]);

  const handlePublishDraft = async (e: React.MouseEvent, jobId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Submit this repair job for workshop quotes? It will go live immediately.')) return;
    setPublishing(jobId);
    try {
      const apiClient = createApiClient(getToken);
      await jobService(apiClient).publishDraft(jobId);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.error ?? err?.message ?? 'Failed to publish draft.');
    } finally {
      setPublishing(null);
    }
  };

  const title = mode === 'quoted' ? 'My Quoted Jobs' : 'My Jobs';
  const subtitle = mode === 'quoted'
    ? 'Jobs where you currently have a pending quote'
    : 'All jobs associated with your account';

  // For quoted mode show a flat list; for others show tabbed by status
  const filtered = mode === 'quoted'
    ? jobs
    : tab === 'active'
      ? jobs.filter(j => ACTIVE_STATUSES.has(j.status))
      : tab === 'drafts'
        ? jobs.filter(j => j.status === JobStatus.Draft)
        : tab === 'closed'
          ? jobs.filter(j => CLOSED_STATUSES.has(j.status))
          : jobs.filter(j => CANCELLED_STATUSES.has(j.status));

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/my-dashboard" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
          ← Dashboard
        </Link>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>{title}</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>{subtitle}</p>
      </div>

      {/* Tab bar — only for non-quoted mode */}
      {mode !== 'quoted' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {(['active', 'drafts', 'closed', 'cancelled'] as const).map(t => {
            const count = t === 'active'    ? jobs.filter(j => ACTIVE_STATUSES.has(j.status)).length
                        : t === 'drafts'    ? jobs.filter(j => j.status === JobStatus.Draft).length
                        : t === 'closed'   ? jobs.filter(j => CLOSED_STATUSES.has(j.status)).length
                        : jobs.filter(j => CANCELLED_STATUSES.has(j.status)).length;
            const activeColor = t === 'drafts' ? '#d97706' : '#047857';
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '6px 18px', borderRadius: 20, border: '1px solid',
                  fontSize: 13, cursor: 'pointer', fontWeight: tab === t ? 600 : 400,
                  background: tab === t ? activeColor : '#fff',
                  color: tab === t ? '#fff' : '#374151',
                  borderColor: tab === t ? activeColor : '#d1d5db',
                }}
              >
                {t === 'drafts' ? '✏️ Drafts' : t.charAt(0).toUpperCase() + t.slice(1)}
                {' '}
                <span style={{
                  fontSize: 11,
                  background: tab === t ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                  color: tab === t ? '#fff' : '#6b7280',
                  borderRadius: 10, padding: '1px 6px',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Draft notice for carriers */}
      {tab === 'drafts' && filtered.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
          <strong>Draft jobs are private and not yet live.</strong> Click <strong>▶ Publish</strong> on any job to submit it for quotes, or open the job to review and edit it first.
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#dc2626' }}>⚠️ {error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ fontSize: 32, margin: '0 0 8px' }}>📭</p>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>
            {mode === 'quoted' ? 'No pending quoted jobs.' : `No ${tab} jobs.`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(job => {
            const sc = statusColor(job.status);
            return (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px', borderRadius: 10,
                  border: '1px solid #e5e7eb', background: '#fff',
                  textDecoration: 'none', color: 'inherit',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Type badge */}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 8px',
                  borderRadius: 6, background: '#eff6ff', color: '#1d4ed8',
                  flexShrink: 0,
                }}>
                  {JOB_TYPE_LABELS[job.jobType] ?? 'Job'}
                </span>

                {/* Internal jobs sit in the same list as market jobs but behave nothing
                    like them - no quote count will ever appear against them. */}
                {job.isInternal && (
                  <span
                    data-testid={`my-job-internal-${job.id}`}
                    style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px',
                      borderRadius: 6, background: '#d1fae5', color: '#065f46',
                      flexShrink: 0,
                    }}
                  >
                    Internal
                  </span>
                )}

                {/* Title + meta */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize: 14, fontWeight: 600, color: '#111827', margin: 0,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {job.title}
                  </p>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>
                    {job.referenceNumber}
                    {' · '}
                    {job.pickupAddress.split(',').slice(-2).join(',').trim()}
                    {' → '}
                    {job.deliveryAddress.split(',').slice(-2).join(',').trim()}
                    {' · '}
                    {job.distanceMiles.toFixed(1)} mi
                    {job.quoteCount > 0 && ` · ${job.quoteCount} quote${job.quoteCount === 1 ? '' : 's'}`}
                  </p>
                </div>

                {/* Scheduled date + Draft action */}
                <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  {new Date(job.scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {job.status === JobStatus.Draft ? (
                    <button
                      onClick={e => handlePublishDraft(e, job.id)}
                      disabled={publishing === job.id}
                      style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 10px',
                        borderRadius: 12, border: 'none', cursor: 'pointer',
                        background: publishing === job.id ? '#d1d5db' : '#f59e0b',
                        color: '#fff',
                      }}
                    >
                      {publishing === job.id ? 'Publishing…' : '▶ Publish'}
                    </button>
                  ) : (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px',
                      borderRadius: 12, background: sc.bg, color: sc.color,
                    }}>
                      {JOB_STATUS_LABELS[job.status] ?? String(job.status)}
                    </span>
                  )}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
